/**
 * Employee Status Utility Functions
 * Determines employee visibility and status based on various criteria
 */

/**
 * Check if an employee should be hidden from dashboard and reports
 * @param {Object} employee - The employee object
 * @returns {boolean} - True if employee should be hidden, false otherwise
 */
export const isEmployeeHidden = (employee) => {
  if (!employee) return true;

  // Hide if employee status is inactive
  if (employee.status === 'inactive' || employee.status === 'Inactive') {
    return true;
  }

  // Hide if employment status indicates they're no longer active
  const employmentStatus = employee.employmentStatus || employee.employment_status;
  if (employmentStatus) {
    const inactiveStatuses = ['resigned', 'terminated', 'fired', 'left', 'inactive', 'Resigned', 'Terminated', 'Fired', 'Left', 'Inactive'];
    if (inactiveStatuses.includes(employmentStatus)) {
      return true;
    }
  }

  // Hide if there's an explicit hidden flag
  if (employee.isHidden === true || employee.hidden === true) {
    return true;
  }

  // Hide if resigned date exists and is in the past
  if (employee.resignedDate || employee.resigned_date) {
    const resignedDate = new Date(employee.resignedDate || employee.resigned_date);
    if (resignedDate < new Date()) {
      return true;
    }
  }

  // Hide if termination date exists and is in the past
  if (employee.terminationDate || employee.termination_date) {
    const terminationDate = new Date(employee.terminationDate || employee.termination_date);
    if (terminationDate < new Date()) {
      return true;
    }
  }

  // Hide if endDate exists and is in the past (for contract employees)
  if (employee.endDate || employee.end_date) {
    const endDate = new Date(employee.endDate || employee.end_date);
    if (endDate < new Date()) {
      return true;
    }
  }

  // Employee is visible by default
  return false;
};

/**
 * Check if an employee is currently active
 * @param {Object} employee - The employee object
 * @returns {boolean} - True if employee is active, false otherwise
 */
export const isEmployeeActive = (employee) => {
  return !isEmployeeHidden(employee);
};

/**
 * Get employee status label
 * @param {Object} employee - The employee object
 * @returns {string} - Status label
 */
export const getEmployeeStatusLabel = (employee) => {
  if (!employee) return 'Unknown';

  if (isEmployeeHidden(employee)) {
    const employmentStatus = employee.employmentStatus || employee.employment_status;
    if (employmentStatus) {
      return employmentStatus.charAt(0).toUpperCase() + employmentStatus.slice(1).toLowerCase();
    }
    return 'Inactive';
  }

  return 'Active';
};
